import React from 'react';
import { Box, Typography, Row, Col, Loader } from '@lucid/lego';
import type { Theme, VehicleData } from '../types';
import VehicleCard from '../VehicleCard/VehicleCard';
import styles from './SearchResults.module.scss';

type SearchResultsProps = {
  /** Theme for the component */
  theme: Theme;
  /** Array of vehicle results */
  results: VehicleData[];
  /** Whether search is loading */
  isLoading: boolean;
  /** Whether a search has been performed */
  hasSearched: boolean;
  /** Message to show when no results found */
  emptyMessage: string;
  /** Callback when a vehicle card is clicked */
  onCardClick: (asset: VehicleData) => void;
  /** Test ID for testing */
  dataTestId: string;
};

/**
 * Search results component displaying loading, empty, or results state
 * @component
 * @param {SearchResultsProps} props - Component props
 * @returns {JSX.Element} Search results container with vehicles or status message
 * @example
 * <SearchResults
 *   theme="dark"
 *   results={vehicles}
 *   isLoading={false}
 *   hasSearched={true}
 *   emptyMessage="No vehicles found"
 *   onCardClick={handleSelect}
 *   dataTestId="vin-search"
 * />
 */
const SearchResults: React.FC<SearchResultsProps> = ({
  theme,
  results,
  isLoading,
  hasSearched,
  emptyMessage,
  onCardClick,
  dataTestId,
}) => {
  const showResults = hasSearched && !isLoading && results.length > 0;
  const showEmpty = hasSearched && !isLoading && results.length === 0;

  if (isLoading) {
    return (
      <Box className={styles.loaderContainer} data-testid={`${dataTestId}-loader`}>
        <Loader size={48} />
      </Box>
    );
  }

  if (showEmpty) {
    return (
      <Box className={styles.emptyContainer} data-testid={`${dataTestId}-empty`}>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  if (!showResults) {
    return null;
  }

  return (
    <Box className={styles.resultsContainer} data-testid={`${dataTestId}-results`}>
      <Row gutter={[16, 16]}>
        {results.map((asset, index) => (
          <Col xs={12} sm={12} md={12} lg={12} key={asset.id}>
            <VehicleCard
              asset={asset}
              theme={theme}
              onClick={() => onCardClick(asset)}
              dataTestId={`${dataTestId}-card-${index}`}
            />
          </Col>
        ))}
      </Row>
    </Box>
  );
};

export default SearchResults;
